import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Head from 'next/head'
import Script from 'next/script'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import Cookies from 'js-cookie'
import {
  getPolicyDate,
  setPolicyData,
} from '../components/modalPoliticadePrivacidade/functions'
import SetorInformacao from '../components/setorInformacao'
import SetorPromocao from '../components/setorPromocao'
import SetorServicos from '../components/setorServicos'
import { useBlackFriday } from 'src/contexto/hooks/useBlackFriday'
import Layout from '../layouts/padrao'
import { buscaBannerPromocao } from '../services/api'
import { autenticado } from '../services/auth'
import { TIMEOUT_REVALIDATE } from '../util/config'
import { showcaseBuilder } from '../util/vitrine/showcaseBuilder'
import { useBreakpointValue } from '@gazin/ecommerce-ui'
import { getBannersSliderHome } from 'src/services/marketplace-api/banner'
import { DefaultCarousel } from 'src/components/carrosselPrincipal/sliderDefault'
import { verifyIsServiceUnavailable } from 'src/util/ssr/verifyIsServiceUnavailable'
import { useProductShowcases } from 'src/contexto/hooks/useProductShowcases'
import JsonLdScript from 'src/components/jsonLdScript'
import { createDefaultStructuredData } from 'src/util/metadata/google-metadata'

const ProductSection = dynamic(
  () =>
    import('../components/ProductSection').then(
      (module) => module.ProductSection
    ),
  { ssr: false }
)

const ModalPoliticaDePrivacidade = dynamic(
  () => import('../components/modalPoliticadePrivacidade'),
  { ssr: false }
)

export async function getStaticProps() {
  const [bannersPrincipal, bannersPromocao] = await Promise.all([
    getBannersSliderHome(),
    buscaBannerPromocao(),
  ])

  const responseServiceUnavailable = verifyIsServiceUnavailable([
    bannersPrincipal,
    bannersPromocao,
  ])

  if (responseServiceUnavailable) return responseServiceUnavailable

  return {
    props: {
      bannersPrincipal,
      bannersPromocao,
    },
    revalidate: TIMEOUT_REVALIDATE,
  }
}

export default function Home({ bannersPrincipal, bannersPromocao }) {
  const [userId, setUserId] = useState(null)
  const [publicationDatePrivacyPolicy, setPublicationDatePrivacyPolicy] =
    useState(null)
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)

  const router = useRouter()
  const { isBlackFridayTheme } = useBlackFriday()
  const { showcaseMiddle } = useProductShowcases('home')

  const vitrine = useMemo(
    () => showcaseBuilder(showcaseMiddle),
    [showcaseMiddle]
  )

  const isMobile = useBreakpointValue({ base: true, sm: true, md: false })

  useEffect(() => {
    getPrivacyPolicyPublishingDate()
  }, [])

  useEffect(() => {
    setPolicyData(
      setUserId,
      publicationDatePrivacyPolicy,
      setLoading,
      handleOpen,
      router
    )
  }, [publicationDatePrivacyPolicy])

  const ImageTheo = useMemo(() => {
    const avatarTheo = isBlackFridayTheme ? 'blackfriday-theo.png' : 'theo.png'
    return `${process.env.INTERNAL_IMAGES_URL}/images/png/${avatarTheo}`
  }, [isBlackFridayTheme])

  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = (event, reason) => {
    if (reason !== 'backdropClick') {
      setOpen(false)
    }
  }

  const getPrivacyPolicyPublishingDate = useCallback(async () => {
    if (!autenticado()) return

    setPublicationDatePrivacyPolicy(await getPolicyDate())
  }, [])

  return (
    <>
      <Layout whatsAppPopup={true} pageTopButton={true}>
        <Head>
          <meta property="og:image" content={`${ImageTheo}`} key="og:image" />
          <meta
            property="og:image:secure_url"
            content={`${ImageTheo}`}
            key="og:image:secure_url"
          />
          <meta
            property="og:image:type"
            content="image/png"
            key="og:image:type"
          />
          <meta
            property="og:image:width"
            content="405px"
            key="og:image:width"
          />
          <meta
            property="og:image:height"
            content="417px"
            key="og:image:height"
          />
        </Head>

        <JsonLdScript
          id="google-metadata"
          content={createDefaultStructuredData()}
        />

        <Script
          id="google-search-action"
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: `{
              "@context": "https://schema.org/",
              "@type": "WebSite",
              "name": "Gazin",
              "URL": "https://www.gazin.com.br/",
              "potentialAction": [{
                "@type": "SearchAction",
                "target": "https://www.gazin.com.br/busca/{search_term_string}",
                "query-input": "required name=search_term_string"
              }]
            }`,
          }}
        />

        <main>
          {bannersPrincipal && (
            <DefaultCarousel
              banners={bannersPrincipal.data}
              darkTheme={isBlackFridayTheme}
            />
          )}

          {!isMobile && <SetorInformacao darkTheme={isBlackFridayTheme} />}

          {bannersPromocao && bannersPromocao.data?.length > 0 && (
            <SetorPromocao
              banners={bannersPromocao}
              darkTheme={isBlackFridayTheme}
            />
          )}

          {vitrine &&
            vitrine.map((productSection) => (
              <ProductSection
                key={productSection.id}
                title={productSection.title}
                subtitle={productSection.subtitle}
                products={productSection.recommendations}
                darkTheme={isBlackFridayTheme}
              />
            ))}

          <SetorServicos darkTheme={isBlackFridayTheme} />
        </main>
      </Layout>

      {open && (
        <ModalPoliticaDePrivacidade
          open={open}
          handleClose={handleClose}
          userId={userId}
          redirect
        />
      )}
    </>
  )
}
